import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Image,
  Input,
  Link,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { ArrowRight, CalendarDays, Check, Gauge, Package, Phone, Trophy, X } from 'lucide-react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { validateUsername } from '@ame/shared';
import { AnimatedLogo } from '@/components/AnimatedLogo';
import { useAchtergrondkleur } from '@/components/hooks';
import { ActieFout, SpelProvider, useActie, useSpel } from '@/spel';

/** Zo lang na de laatste toetsaanslag wordt het spel opgezocht, zoals in het origineel. */
const ZOEKVERTRAGING = 500;

/** Zo lang is een spelcode (zie `SPEL_ID` in de worker). */
const CODELENGTE = 6;

/** De kleuren van het welkomstscherm: de lucht van de fabriek en het blauw van het logo. */
const LUCHT = '#9cc2ff';
const NACHTBLAUW = '#0b2f55';

/** Waar de schoorstenen van `fabriek-begin.svg` staan, in procenten van het plaatje. */
const SCHOORSTENEN = [
  { links: 48, boven: 5 },
  { links: 77, boven: 19 },
  { links: 86, boven: 31 },
];

/**
 * Het beginscherm (`/`): spelcode en naam invullen, dan als nieuwe speler beginnen of
 * verder spelen onder een bestaande naam. Overgezet uit `Login/UserLogin.tsx`.
 *
 * Het origineel was alleen een formulier linksboven. Nu staat links een welkom bij de
 * koekjesfabriek (de punten uit de introductievideo, zie de spelleiderhandleiding) en
 * rechts een gewoon inlogscherm met het AME-logo.
 *
 * Het spel wordt een halve seconde na het typen opgezocht door ermee te verbinden; de
 * `SpelProvider` hieronder krijgt dus steeds het laatst getypte id.
 */
export function UserLogin() {
  useAchtergrondkleur(LUCHT);
  const [gameId, setGameId] = useState('');
  const [username, setUsername] = useState('');
  // Spelcodes zijn kleine letters en cijfers; een spatie of hoofdletter is een tikfout.
  const spelId = gameId.trim().toLowerCase();
  const gezochtId = useVertraagd(spelId, ZOEKVERTRAGING);

  return (
    <Flex h="full" overflowY="auto" direction={{ base: 'column', lg: 'row' }}>
      <Welkom />
      <Flex
        as="main"
        // Op een telefoon eerst inloggen, daarna pas waar het spel over gaat.
        order={{ base: -1, lg: 0 }}
        w={{ base: 'full', lg: '480px' }}
        flexShrink={0}
        bg="white"
        alignItems="center"
        justifyContent="center"
        px={{ base: '6', md: '10' }}
        py={{ base: '10', lg: '12' }}
        boxShadow={{ lg: '-12px 0 40px rgba(11, 47, 85, 0.12)' }}
      >
        <SpelProvider spelId={gezochtId}>
          <Inlogformulier
            gameId={gameId}
            setGameId={setGameId}
            username={username}
            setUsername={setUsername}
            zoekt={gezochtId !== spelId}
          />
        </SpelProvider>
      </Flex>
    </Flex>
  );
}

/** Links: waar het spel over gaat, met de fabriek. */
function Welkom() {
  return (
    <Flex
      as="section"
      aria-label="Over het spel"
      // Onder elkaar (telefoon) zo hoog als de inhoud; anders krimpt het blok en valt de
      // tekst eraf.
      flex={{ base: 'none', lg: '1' }}
      minW="0"
      direction="column"
      justifyContent="center"
      gap={{ base: '6', lg: '8' }}
      px={{ base: '6', md: '12', xl: '20' }}
      py={{ base: '8', lg: '12' }}
      bgGradient="to-b"
      gradientFrom="#dbe9ff"
      gradientTo={LUCHT}
      color={NACHTBLAUW}
      overflow="hidden"
    >
      <Box maxW="2xl">
        <Text
          textStyle="sm"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="widest"
          opacity="0.75"
        >
          Asset Management Experience
        </Text>
        <Heading
          as="h1"
          fontSize={{ base: '4xl', md: '5xl', xl: '6xl' }}
          lineHeight="1"
          fontWeight="bold"
          mt="3"
          textWrap="balance"
        >
          Red de koekjesfabriek
        </Heading>
        <Text textStyle={{ base: 'md', md: 'lg' }} mt="4" maxW="xl">
          Jullie zijn het nieuwe team asset managers van &apos;t Woud Stammetjes. Houd de machines
          draaiend, los storingen en incidenten op en haal het productiedoel, binnen het budget.
        </Text>
      </Box>

      <Fabriek />

      <Flex as="ul" listStyleType="none" m="0" p="0" gap="2" flexWrap="wrap" maxW="2xl">
        <Kenmerk icoon={<Package size="16" />}>Productiedoel in pallets</Kenmerk>
        <Kenmerk icoon={<Gauge size="16" />}>Beschikbaarheid hoog houden</Kenmerk>
        <Kenmerk icoon={<CalendarDays size="16" />}>Elke ronde is een week</Kenmerk>
        <Kenmerk icoon={<Phone size="16" />}>Stakeholders bellen op</Kenmerk>
      </Flex>
    </Flex>
  );
}

/** De fabriek die zachtjes zweeft, met rook uit de schoorstenen. Alleen decoratie. */
function Fabriek() {
  return (
    <Box
      aria-hidden
      position="relative"
      w={{ base: '72', md: 'md', lg: 'auto' }}
      maxW="full"
      alignSelf={{ base: 'center', lg: 'flex-start' }}
      animation="zweven 6s ease-in-out infinite"
      _motionReduce={{ animation: 'none' }}
    >
      {/* Naast elkaar schaalt de fabriek mee met de hoogte van het scherm, zodat de titel en
          de kenmerken op een lager laptopscherm niet van de pagina vallen. */}
      <Image
        src="/icons/fabriek-begin.svg"
        alt=""
        w={{ base: 'full', lg: 'auto' }}
        h={{ lg: '38vh' }}
        maxH={{ lg: '440px' }}
      />
      {SCHOORSTENEN.flatMap(({ links, boven }, schoorsteen) =>
        [0, 1, 2].map((wolk) => (
          <Box
            key={`${schoorsteen}-${wolk}`}
            position="absolute"
            left={`${links}%`}
            top={`${boven}%`}
            boxSize={{ base: '4', md: '6' }}
            ms={{ base: '-2', md: '-3' }}
            borderRadius="full"
            bg="whiteAlpha.900"
            filter="blur(2px)"
            opacity="0"
            animation={`rook 4.5s ease-out ${schoorsteen * 0.7 + wolk * 1.5}s infinite`}
            _motionReduce={{ display: 'none' }}
          />
        )),
      )}
    </Box>
  );
}

function Kenmerk({ icoon, children }: { icoon: ReactNode; children: string }) {
  return (
    <Flex
      as="li"
      alignItems="center"
      gap="2"
      bg="whiteAlpha.700"
      borderRadius="full"
      px="3.5"
      py="1.5"
      textStyle="sm"
      fontWeight="semibold"
    >
      <Box as="span" aria-hidden display="inline-flex" opacity="0.8">
        {icoon}
      </Box>
      {children}
    </Flex>
  );
}

type FormulierProps = {
  gameId: string;
  setGameId: (waarde: string) => void;
  username: string;
  setUsername: (waarde: string) => void;
  /** Het getypte id is nog niet opgezocht. */
  zoekt: boolean;
};

function Inlogformulier({ gameId, setGameId, username, setUsername, zoekt }: FormulierProps) {
  const { staat, status } = useSpel();
  const actie = useActie();
  const navigate = useNavigate();
  const [fout, setFout] = useState('');
  const [bezig, setBezig] = useState(false);

  const game = zoekt ? undefined : staat?.game;
  // Niet in het origineel: zonder spaties eromheen. Een naam van alleen spaties kan niet.
  const naam = username.trim();
  // Direct op de getypte naam en niet vertraagd zoals in het origineel: het is geen
  // serververzoek meer. Zo hoort de knoptekst altijd bij de naam in het veld.
  const { usernameExists, playerId } = validateUsername(staat ?? undefined, naam);
  // Niet in het origineel: hoofdletters tellen niet ("anna" is Anna). Toon de naam zoals die
  // in het spel staat.
  const bekendeNaam =
    (usernameExists && staat?.players.players.find((speler) => speler._id === playerId)?.name) ||
    naam;
  const spelVol = !!game && !!naam && !playerId;
  const binnen = game
    ? (staat?.players.players.flatMap((speler) => (speler.name ? [speler.name] : [])) ?? [])
    : [];
  const plekken = staat?.players.players.length ?? 0;

  async function logIn(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    if (!game || !naam || !playerId) return;
    if (usernameExists) {
      navigate(`/${game._id}/${playerId}`);
      return;
    }
    setBezig(true);
    try {
      // Het origineel stuurde `setPlayerName` met de vrije plek die de browser zag. Typen
      // twee spelers tegelijk een nieuwe naam, dan kregen ze dezelfde plek en overschreef
      // de tweede de eerste. `createPlayer` kiest de plek op de server en geeft het id terug.
      const nieuwId = await actie('createPlayer', naam);
      setFout('');
      navigate(`/${game._id}/${nieuwId}`);
    } catch (reden) {
      setFout(foutmelding(reden));
    } finally {
      setBezig(false);
    }
  }

  const code = gameId.trim();

  return (
    <Flex as="form" onSubmit={logIn} direction="column" gap="6" w="full" maxW="sm">
      <Flex alignItems="center" gap="4">
        <Box
          boxSize="16"
          flexShrink={0}
          borderRadius="md"
          overflow="hidden"
          css={{ '& img': { width: '100%', height: 'auto' } }}
        >
          <AnimatedLogo />
        </Box>
        <Box>
          <Heading as="h2" textStyle="2xl" fontWeight="bold" color={NACHTBLAUW}>
            Doe mee
          </Heading>
          <Text textStyle="sm" color="gray.600">
            Vul de spelcode van je spelleider in, daarna je naam.
          </Text>
        </Box>
      </Flex>

      <Field.Root>
        <Field.Label fontWeight="semibold" color="gray.800">
          Spelcode
        </Field.Label>
        <Input
          value={gameId}
          onChange={(e) => setGameId(e.target.value.replace(/\s/g, '').toLowerCase())}
          placeholder="ab12cd"
          maxLength={CODELENGTE}
          size="xl"
          fontFamily="mono"
          letterSpacing="0.4em"
          textAlign="center"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          autoFocus
          borderColor={game ? 'green.500' : undefined}
        />
        <Box aria-live="polite" minH="5" mt="1">
          <Spelstatus
            code={code}
            zoekt={zoekt || status === 'verbinden'}
            gevonden={!!game}
            binnen={binnen}
            plekken={plekken}
          />
        </Box>
      </Field.Root>

      <Field.Root invalid={spelVol}>
        <Field.Label fontWeight="semibold" color="gray.800">
          Je naam
        </Field.Label>
        <Input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Bijvoorbeeld Anna"
          size="xl"
          autoComplete="off"
        />
        {spelVol ? (
          <Field.ErrorText>Alle plekken in dit spel zijn al bezet.</Field.ErrorText>
        ) : game && usernameExists ? (
          <Field.HelperText color="green.700">
            Welkom terug, {bekendeNaam}. Je speelt verder waar je was.
          </Field.HelperText>
        ) : (
          <Field.HelperText>
            Speel je verder op een andere computer? Gebruik dezelfde naam.
          </Field.HelperText>
        )}
      </Field.Root>

      <Button
        type="submit"
        size="xl"
        colorPalette="ameBlue"
        disabled={!game || !naam || !playerId}
        loading={bezig}
      >
        {usernameExists ? `Speel verder als ${bekendeNaam}!` : 'Log in als nieuwe speler!'}
        <ArrowRight aria-hidden />
      </Button>

      {fout && (
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Title>{fout}</Alert.Title>
        </Alert.Root>
      )}

      {/* Niet in het origineel: de highscores van alle afgeronde spellen. */}
      <Link asChild alignSelf="center" color="ameBlue.600" textStyle="sm">
        <RouterLink to="/highscores">
          <Trophy aria-hidden size="1em" />
          Highscores
        </RouterLink>
      </Link>
    </Flex>
  );
}

/**
 * De tekst van het origineel als de naam net door een ander is genomen; anders de melding
 * van de server (geen plek meer) of van de verbinding (time-out, verbroken).
 */
function foutmelding(reden: unknown): string {
  if (
    reden instanceof ActieFout &&
    reden.soort === 'spel' &&
    reden.message === 'Deze naam bestaat al!'
  ) {
    return 'Deze username is al in gebruik.';
  }
  return reden instanceof Error ? reden.message : String(reden);
}

type StatusProps = {
  code: string;
  zoekt: boolean;
  gevonden: boolean;
  /** De namen van wie al is ingelogd. */
  binnen: string[];
  plekken: number;
};

/**
 * Onder de spelcode: hoeveel tekens er nog moeten, of het spel gezocht of gevonden is en wie
 * er al in zit. In het origineel alleen een vinkje of kruisje in het veld.
 */
function Spelstatus({ code, zoekt, gevonden, binnen, plekken }: StatusProps) {
  if (code.length === 0) {
    return (
      <Text textStyle="sm" color="gray.500">
        Zes letters en cijfers.
      </Text>
    );
  }
  if (code.length < CODELENGTE) {
    const nog = CODELENGTE - code.length;
    return (
      <Text textStyle="sm" color="gray.500">
        Nog {nog} {nog === 1 ? 'teken' : 'tekens'}.
      </Text>
    );
  }
  if (zoekt) {
    return (
      <Flex alignItems="center" gap="2" textStyle="sm" color="gray.600">
        <Spinner size="xs" aria-hidden />
        Spel zoeken...
      </Flex>
    );
  }
  if (!gevonden) {
    return (
      <Flex alignItems="center" gap="1.5" textStyle="sm" color="red.600">
        <X size="16" aria-hidden />
        Geen spel met deze code. Kijk de code na bij je spelleider.
      </Flex>
    );
  }
  return (
    <Box textStyle="sm">
      <Flex alignItems="center" gap="1.5" color="green.700" fontWeight="semibold">
        <Check size="16" aria-hidden />
        Spel gevonden: {binnen.length} van {plekken} spelers binnen
      </Flex>
      {binnen.length > 0 && (
        <Flex gap="1.5" flexWrap="wrap" mt="2">
          {binnen.map((naam) => (
            <Text
              key={naam}
              as="span"
              bg="gray.100"
              color="gray.700"
              borderRadius="full"
              px="2.5"
              py="0.5"
              textStyle="xs"
            >
              {naam}
            </Text>
          ))}
        </Flex>
      )}
    </Box>
  );
}

/** `waarde`, maar pas als hij `vertraging` ms niet veranderd is. */
function useVertraagd<T>(waarde: T, vertraging: number): T {
  const [vertraagd, setVertraagd] = useState(waarde);
  useEffect(() => {
    const timer = setTimeout(() => setVertraagd(waarde), vertraging);
    return () => clearTimeout(timer);
  }, [waarde, vertraging]);
  return vertraagd;
}
